/**
 * GeoBlockGuard - Client-side geo-blocking component
 * 
 * Wraps protected routes to prevent access from blocked countries
 * BEFORE any authenticated UI or sensitive data is rendered.
 * 
 * This is a defense-in-depth measure. Primary geo-blocking should
 * be at the edge (Cloudflare/Vercel WAF).
 */

import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert, Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  checkGeoBlock, 
  checkGeoBlockSync, 
  isGeoBlockedRoute,
  BLOCKED_COUNTRIES,
  type GeoCheckResult 
} from '@/lib/geoBlock';

interface GeoBlockGuardProps {
  children: ReactNode;
}

/**
 * 451 Unavailable For Legal Reasons component
 */
function GeoBlockedPage({ country }: { country: string | null }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldAlert className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">451 - Unavailable</CardTitle>
          <CardDescription className="text-base">
            Service not available in your region
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Globe className="h-4 w-4" />
            <span className="text-sm">
              Detected region: {country || 'Unknown'}
            </span>
          </div>
          
          <p className="text-sm text-muted-foreground text-center">
            Access to this service is restricted based on geographic location
            due to regulatory requirements.
          </p>
          
          <p className="text-xs text-muted-foreground text-center pt-4">
            If you believe this is an error, please contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * GeoBlockGuard component
 * Place at the top of your route tree for protected routes
 */
export function GeoBlockGuard({ children }: GeoBlockGuardProps) {
  const location = useLocation();
  const [geoResult, setGeoResult] = useState<GeoCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Quick sync check first (uses cached country)
    const syncResult = checkGeoBlockSync();
    
    if (syncResult.blocked) {
      setGeoResult(syncResult);
      setIsChecking(false);
      return;
    }

    // Only do async check for protected routes
    if (!isGeoBlockedRoute(location.pathname)) {
      setGeoResult({ blocked: false, country: null });
      setIsChecking(false);
      return;
    }

    // Async check for accurate result
    checkGeoBlock().then((result) => {
      setGeoResult(result);
      setIsChecking(false);
    });
  }, [location.pathname]);

  // Show loader while checking geo
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Block if geo-check indicates blocked country
  if (geoResult?.blocked) {
    return <GeoBlockedPage country={geoResult.country} />;
  }

  return <>{children}</>;
}

/**
 * Hook for components that need geo-block awareness
 */
export function useGeoBlock() {
  const [geoResult, setGeoResult] = useState<GeoCheckResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkGeoBlock().then((result) => {
      setGeoResult(result);
      setIsLoading(false);
    });
  }, []);

  return {
    isBlocked: geoResult?.blocked ?? false,
    country: geoResult?.country ?? null,
    isLoading,
  };
}

export { BLOCKED_COUNTRIES };
